// src/components/modals/BICredentialsModal.jsx

import React, { useState } from 'react';
import {
  Key, AlertTriangle, Copy, Check, Eye, EyeOff,
} from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';

/**
 * One-time display of BI access credentials after provision or rotate.
 *
 * Security pattern (matches Stripe / AWS / Vercel / GitHub):
 *   - Forces user to acknowledge they've saved credentials before dismiss
 *   - No click-outside-to-close (the password is critical to capture)
 *   - Password hidden by default; user must click to reveal
 *   - Copy-to-clipboard buttons for each sensitive field
 *   - Warning that the password won't be shown again
 *
 * Props:
 *   credentials  - object with role_name, password, host, port, database,
 *                  schema_name, ssl_mode, connection_string
 *   action       - "provisioned" or "rotated" (changes the header)
 *   onClose      - called when user dismisses (only after checkbox)
 */
function CredentialField({ label, value, copiedField, onCopy, field, copyable = true }) {
  return (
    <div>
      <label className="text-xs text-[#a0a0a0] uppercase tracking-wide">
        {label}
      </label>
      <div className="mt-1 flex items-center gap-2">
        <div className="flex-1 px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg font-mono text-sm text-white break-all min-h-[2.5rem] flex items-center">
          {value}
        </div>
        {copyable && (
          <button
            onClick={() => onCopy(field, value)}
            className="h-10 w-10 flex items-center justify-center bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-[#a0a0a0] hover:text-white hover:bg-[#222] transition-colors"
            aria-label={`Copy ${label.toLowerCase()}`}
            type="button"
          >
            {copiedField === field ? (
              <Check className="h-4 w-4 text-green-400" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export default function BICredentialsModal({ credentials, action, onClose }) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const handleCopy = async (field, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Clipboard write failed', err);
    }
  };

  const headerTitle = action === 'rotated' ? 'Password Rotated' : 'BI Access Provisioned';
  const subtitle = action === 'rotated'
    ? 'New password generated. The old password no longer works.'
    : 'Use these credentials to connect Power BI, Tableau, or any Postgres BI tool.';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="w-full max-w-2xl bg-[#0f0f0f] border border-[#2a2a2a] rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="p-6 border-b border-[#2a2a2a]">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center justify-center flex-shrink-0">
              <Key className="h-5 w-5 text-green-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white">{headerTitle}</h3>
              <p className="text-sm text-[#a0a0a0] mt-0.5">{subtitle}</p>
            </div>
          </div>
        </div>

        {/* Warning banner */}
        <div className="px-6 pt-4">
          <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-200">
              <strong className="font-semibold">Save your password now.</strong>
              {' '}
              This is the only time it will be shown. If lost, you must rotate to a new one.
            </p>
          </div>
        </div>

        {/* Credentials fields */}
        <div className="p-6 space-y-3">
          <CredentialField
            label="Host"
            value={credentials.host}
            copiedField={copiedField}
            onCopy={handleCopy}
            field="host"
          />
          <CredentialField
            label="Port"
            value={String(credentials.port)}
            copiedField={copiedField}
            onCopy={handleCopy}
            field="port"
          />
          <CredentialField
            label="Database"
            value={credentials.database}
            copiedField={copiedField}
            onCopy={handleCopy}
            field="database"
          />
          <CredentialField
            label="Username"
            value={credentials.username}
            copiedField={copiedField}
            onCopy={handleCopy}
            field="username"
          />

          {/* Password (special: masked, with show/hide toggle) */}
          <div>
            <label className="text-xs text-[#a0a0a0] uppercase tracking-wide">
              Password
            </label>
            <div className="mt-1 flex items-center gap-2">
              <div className="flex-1 px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg font-mono text-sm text-white break-all min-h-[2.5rem] flex items-center">
                {showPassword ? credentials.password : '\u2022'.repeat(43)}
              </div>
              <button
                onClick={() => setShowPassword(!showPassword)}
                className="h-10 w-10 flex items-center justify-center bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-[#a0a0a0] hover:text-white hover:bg-[#222] transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                type="button"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              <button
                onClick={() => handleCopy('password', credentials.password)}
                className="h-10 w-10 flex items-center justify-center bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-[#a0a0a0] hover:text-white hover:bg-[#222] transition-colors"
                aria-label="Copy password"
                type="button"
              >
                {copiedField === 'password' ? (
                  <Check className="h-4 w-4 text-green-400" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>

          <CredentialField
            label="Schema"
            value={credentials.schema_name}
            copiedField={copiedField}
            onCopy={handleCopy}
            field="schema"
          />

          <CredentialField
            label="SSL Mode"
            value={credentials.ssl_mode || 'require'}
            copiedField={copiedField}
            onCopy={handleCopy}
            field="ssl_mode"
            copyable={false}
          />

          {/* Connection string */}
          <div>
            <label className="text-xs text-[#a0a0a0] uppercase tracking-wide">
              Connection String (one-click copy)
            </label>
            <div className="mt-1 flex items-start gap-2">
              <div className="flex-1 px-3 py-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg font-mono text-xs text-white break-all">
                {credentials.connection_string}
              </div>
              <button
                onClick={() => handleCopy('connection_string', credentials.connection_string)}
                className="h-10 w-10 flex items-center justify-center bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg text-[#a0a0a0] hover:text-white hover:bg-[#222] transition-colors flex-shrink-0"
                aria-label="Copy connection string"
                type="button"
              >
                {copiedField === 'connection_string' ? (
                  <Check className="h-4 w-4 text-green-400" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Acknowledgement + close */}
        <div className="px-6 pb-6 border-t border-[#2a2a2a] pt-4">
          <label className="flex items-start gap-3 cursor-pointer select-none mb-4">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-[#2a2a2a] bg-[#1a1a1a] text-blue-500 focus:ring-blue-500 cursor-pointer"
            />
            <span className="text-sm text-white">
              I have saved my password to a secure location. I understand this
              password cannot be recovered, only rotated.
            </span>
          </label>

          <div className="flex justify-end">
            <Button
              onClick={onClose}
              disabled={!acknowledged}
              variant={acknowledged ? 'default' : 'ghost'}
            >
              Close
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
}